<template>
    <a-modal
        :visible="true"
        width="60vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="pgm-wrap"
        @cancel="$emit('close')"
    >
        <div class="pgm-modal">
            <div class="pgm-head">
                <div>
                    <h3>{{ currentGroup ? currentGroup.descricao : "Grupos de produtos" }}</h3>
                    <div class="pgm-sub">
                        {{ currentGroup ? "Produtos do grupo" : "Organize seus produtos em grupos" }}
                    </div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="pgm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <template v-if="!currentGroup">
                <div class="pgm-tools">
                    <a-input v-model="groupSearch" class="pgm-search" placeholder="Buscar grupo" allow-clear>
                        <a-icon slot="prefix" type="search" />
                    </a-input>
                    <a-input-search
                        v-model="newGroupName"
                        class="pgm-search"
                        placeholder="Nome do novo grupo"
                        :loading="creating"
                        @search="createGroup"
                    >
                        <a-button slot="enterButton" type="primary" icon="plus" />
                    </a-input-search>
                </div>

                <div class="pgm-body">
                    <a-table
                        size="small"
                        :columns="groupColumns"
                        :data-source="filteredGroups"
                        row-key="id"
                        :loading="loadingGroups"
                        :pagination="{ pageSize: 20, size: 'small' }"
                        :locale="{ emptyText: 'Nenhum grupo cadastrado.' }"
                    >
                        <template slot="id" slot-scope="text">#{{ text }}</template>
                        <template slot="nome" slot-scope="text, group">
                            <a-input
                                v-if="editingGroupId === group.id"
                                v-model="editingName"
                                size="small"
                                @pressEnter="saveGroupName(group)"
                            />
                            <a v-else class="pgm-link" @click="openGroup(group)">{{ group.descricao }}</a>
                        </template>
                        <template slot="acoes" slot-scope="text, group">
                            <ActionMenu :actions="getGroupActions(group)" />
                        </template>
                    </a-table>
                </div>
            </template>

            <template v-else>
                <div class="pgm-tools">
                    <a-button @click="currentGroup = null">
                        <a-icon type="arrow-left" /> Voltar
                    </a-button>
                    <a-input
                        v-model="productSearch"
                        class="pgm-search"
                        placeholder="Buscar produto"
                        allow-clear
                        @change="onProductSearch"
                        @pressEnter="loadProducts(1)"
                    >
                        <a-icon slot="prefix" type="search" />
                    </a-input>
                    <a-button class="pgm-tools-right" :loading="exporting" @click="exportExcel">
                        <i class="fa-solid fa-file-excel pgm-btn-icon"></i> Excel
                    </a-button>
                </div>

                <div class="pgm-body">
                    <a-table
                        size="small"
                        :columns="productColumns"
                        :data-source="products"
                        row-key="id"
                        :loading="loadingProducts"
                        :pagination="productsPagination"
                        :locale="{ emptyText: 'Nenhum produto neste grupo.' }"
                        @change="(p) => loadProducts(p.current)"
                    >
                        <template slot="ean" slot-scope="text, item">{{ item.produto ? item.produto.pro_ean : "--" }}</template>
                        <template slot="nome" slot-scope="text, item">{{ item.produto ? item.produto.pro_descricao : "--" }}</template>
                        <template slot="acoes" slot-scope="text, item">
                            <a-tooltip title="Remover do grupo">
                                <a-button size="small" class="pgm-icon-btn" :loading="removingId === item.id" @click="removeProduct(item)">
                                    <span class="material-icons-outlined pgm-danger">delete</span>
                                </a-button>
                            </a-tooltip>
                        </template>
                    </a-table>
                </div>
            </template>

            <div class="pgm-foot">
                <a-button @click="$emit('close')">Fechar</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import { mapState } from "vuex";
import ProductGroupService from "@/services/v3/management/ProductGroupService.js";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import ExcelDownload from "@/services/ExcelDownload.js";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";

export default {
    name: "ProductGroupsV2Modal",
    components: { ActionMenu },
    data() {
        return {
            service: new ProductGroupService(),
            groups: [],
            loadingGroups: false,
            groupSearch: "",
            newGroupName: "",
            creating: false,
            editingGroupId: null,
            editingName: "",
            currentGroup: null,
            products: [],
            loadingProducts: false,
            productSearch: "",
            productSearchTimeout: null,
            removingId: null,
            exporting: false,
            productsPagination: {
                current: 1,
                pageSize: 30,
                total: 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
            },
            groupColumns: [
                { title: "ID", dataIndex: "id", key: "id", width: 80, scopedSlots: { customRender: "id" } },
                { title: "Nome", dataIndex: "descricao", key: "descricao", scopedSlots: { customRender: "nome" } },
                { title: "Produtos", dataIndex: "count", key: "count", align: "center", width: 100 },
                { title: "Ações", key: "acoes", align: "center", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
            productColumns: [
                { title: "Cód.", key: "ean", scopedSlots: { customRender: "ean" } },
                { title: "Nome", key: "nome", scopedSlots: { customRender: "nome" } },
                { title: "Ação", key: "acoes", align: "center", width: 70, scopedSlots: { customRender: "acoes" } },
            ],
        };
    },
    computed: {
        ...mapState(["whitelabel"]),
        filteredGroups() {
            const term = (this.groupSearch || "").trim().toLowerCase();
            if (!term) return this.groups;
            return this.groups.filter((g) => String(g.descricao || "").toLowerCase().includes(term));
        },
    },
    created() {
        this.loadGroups();
    },
    beforeDestroy() {
        clearTimeout(this.productSearchTimeout);
    },
    methods: {
        loadGroups() {
            this.loadingGroups = true;
            return this.service
                .loadProductGroups()
                .then(({ data }) => {
                    this.groups = Array.isArray(data) ? data : [];
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loadingGroups = false;
                });
        },
        createGroup() {
            const name = (this.newGroupName || "").trim();
            if (!name) return;
            this.creating = true;
            this.service
                .createProductGroups(name)
                .then(() => {
                    this.newGroupName = "";
                    this.$emit("updated");
                    return this.loadGroups();
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.creating = false;
                });
        },
        getGroupActions(group) {
            const editing = this.editingGroupId === group.id;
            return [
                { label: "Ver produtos", icon: "fact_check", iconClass: "action-teal", onClick: () => this.openGroup(group) },
                editing
                    ? { label: "Salvar nome", icon: "save", iconClass: "action-success", onClick: () => this.saveGroupName(group) }
                    : { label: "Renomear grupo", icon: "edit", iconClass: "action-info", onClick: () => this.startEditing(group) },
            ];
        },
        startEditing(group) {
            this.editingGroupId = group.id;
            this.editingName = group.descricao;
        },
        saveGroupName(group) {
            const name = (this.editingName || "").trim();
            if (!name) return;
            this.service
                .updateGroup(name, group.id)
                .then(() => {
                    group.descricao = name;
                    this.editingGroupId = null;
                    this.$emit("updated");
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        openGroup(group) {
            this.currentGroup = group;
            this.productSearch = "";
            this.products = [];
            this.loadProducts(1);
        },
        onProductSearch() {
            clearTimeout(this.productSearchTimeout);
            this.productSearchTimeout = setTimeout(() => this.loadProducts(1), 500);
        },
        loadProducts(page) {
            clearTimeout(this.productSearchTimeout);
            this.loadingProducts = true;
            this.service
                .getGroupProducts(this.currentGroup.id, page, this.productSearch)
                .then(({ data }) => {
                    this.products = data.data || [];
                    this.productsPagination.total = data.total || 0;
                    this.productsPagination.current = page;
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.loadingProducts = false;
                });
        },
        removeProduct(item) {
            this.removingId = item.id;
            this.service
                .deleteProduct(item.id)
                .then(() => {
                    this.products = this.products.filter((p) => p.id !== item.id);
                    this.productsPagination.total = Math.max(0, this.productsPagination.total - 1);
                    this.currentGroup.count = Math.max(0, (this.currentGroup.count || 1) - 1);
                    this.$emit("updated");
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.removingId = null;
                });
        },
        exportExcel() {
            this.exporting = true;
            this.service
                .getGroupProducts(this.currentGroup.id, 1, this.productSearch, true)
                .then(({ data }) => {
                    const primary = this.whitelabel && this.whitelabel.data.colors.primary;
                    new ExcelDownload({
                        tableName: "Produtos",
                        groupName: this.currentGroup.descricao,
                        userName: localStorage.getItem("user_name"),
                        author: this.whitelabel && this.whitelabel.data.providers.text_window,
                        headerColor: primary,
                        store: localStorage.getItem("grupo"),
                        header: ["Código EAN", "Código Interno", "Descrição", "Embalagem"],
                        body: (Array.isArray(data) ? data : data.data || []).map((item) => [
                            item.produto.pro_ean,
                            item.produto.pro_id,
                            item.produto.pro_descricao,
                            item.produto.pro_generico == 1 ? item.produto.pro_unidade_compra : item.produto.pro_embalagem,
                        ]),
                        colWidths: [20, 25, 40, 20],
                        styles: {
                            headerStyle: {
                                alignment: { horizontal: "center", vertical: "center" },
                                font: { bold: true, color: { rgb: "FFFFFF" } },
                                fill: { fgColor: { rgb: primary ? primary.replace("#", "") : "FF7110" } },
                            },
                            bodyStyle: {
                                alignment: { horizontal: "left", vertical: "center", wrapText: true },
                                font: { sz: 11 },
                            },
                        },
                    });
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                })
                .finally(() => {
                    this.exporting = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.pgm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.pgm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.pgm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.pgm-x.ant-btn,
.pgm-x.ant-btn:focus,
.pgm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.pgm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.pgm-tools {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 20px 12px;
}
.pgm-search {
    flex: 1 1 220px;
    max-width: 320px;
}
.pgm-tools-right {
    margin-left: auto;
}
.pgm-btn-icon {
    margin-right: 6px;
}
.pgm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 200px;
}
.pgm-link {
    font-weight: 600;
    color: var(--primary-color);
}
.pgm-icon-btn.ant-btn {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    box-shadow: none;
    background: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
.pgm-danger {
    color: #ff4d4f;
    font-size: 18px;
}
.pgm-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
</style>

<style lang="scss">
.pgm-wrap {
    .ant-modal {
        min-width: 560px;
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
